import {
  displayUserEmail,
  displayUserName,
} from "@modules/user/deletedUser";
import React from "react";
import { format } from "date-fns";
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronDown } from "lucide-react";

import { UserRole } from "@modules/authentication/roles";
import type { UserSortKey } from "@modules/user/user.service";
import { capitalisedSentence } from "@util/string.util";
import { cn } from "~/lib/utils";
import { Card, CardContent } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Button } from "~/components/ui/button";
import { CopyButton } from "~/components/ui/copy-button";
import { SelectionCombobox } from "~/components/ui/selection-combobox";
import { Badge } from "~/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from "~/components/ui/pagination";
import {
  ALL_ROLES,
  ROLE_FILTER_OPTIONS,
  type UsersTableState,
} from "@modules/user/useUsersTable";
import {
  getRoleBadgeProps,
  type UserViewModel,
} from "@modules/user/userViewModel";

export interface UsersTableProps {
  table: UsersTableState;
  /** Replaces the default read-only role badge, e.g. with an editable role control. */
  renderRole?: (user: UserViewModel) => React.ReactNode;
  /** When provided, an Actions column is rendered with this content per row. */
  renderActions?: (user: UserViewModel) => React.ReactNode;
}

const SORTABLE_COLUMNS: Array<{ key: UserSortKey; label: string }> = [
  { key: "fullName", label: "Name" },
  { key: "email", label: "Email" },
  { key: "createdAt", label: "Signed up at" },
];

export const RoleBadge: React.FC<{ role: UserRole }> = ({ role }) => (
  <Badge {...getRoleBadgeProps(role)}>{capitalisedSentence(role)}</Badge>
);

export const UsersTable: React.FC<UsersTableProps> = ({
  table,
  renderRole,
  renderActions,
}) => {
  const {
    users,
    isUsersLoading,
    page,
    hasNextPage,
    expandedUserIds,
    searchTerm,
    selectedRoleLabel,
    selectedOrganisationLabel,
    organisationFilterOptions,
    sortKey,
    sortDirection,
    setSearchTerm,
    setSelectedRole,
    setSelectedOrganisationId,
    handleSort,
    goToPreviousPage,
    goToNextPage,
    toggleExpandedUser,
  } = table;

  const hasActions = Boolean(renderActions);
  const columnCount = hasActions ? 5 : 4;

  const renderSortIcon = (key: UserSortKey) => {
    if (sortKey !== key) {
      return <ArrowUpDown className="size-4 text-muted-foreground" />;
    }

    return sortDirection === "ASC" ? (
      <ArrowUp className="size-4" />
    ) : (
      <ArrowDown className="size-4" />
    );
  };

  return (
    <>
      <Card>
        <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div className="flex flex-col gap-2">
            <Label
              htmlFor="users-search"
              className="text-sm font-semibold text-foreground"
            >
              Search users
            </Label>
            <Input
              id="users-search"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search by name or email"
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label className="text-sm font-semibold text-foreground">
              Role
            </Label>
            <SelectionCombobox
              placeholder={selectedRoleLabel}
              options={ROLE_FILTER_OPTIONS}
              onSelect={(value) =>
                setSelectedRole(value as UserRole | typeof ALL_ROLES)
              }
              searchPlaceholder="Search roles..."
              triggerClassName="w-full justify-between"
              contentClassName="w-[260px]"
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label className="text-sm font-semibold text-foreground">
              Organisation
            </Label>
            <SelectionCombobox
              placeholder={selectedOrganisationLabel}
              options={organisationFilterOptions}
              onSelect={setSelectedOrganisationId}
              searchPlaceholder="Search organisations..."
              triggerClassName="w-full justify-between"
              contentClassName="w-[300px]"
            />
          </div>
        </CardContent>
      </Card>
      <Card className="py-0">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                {SORTABLE_COLUMNS.map((column) => (
                  <TableHead key={column.key}>
                    <Button
                      type="button"
                      variant="ghost"
                      className="flex w-full items-center justify-between gap-2 px-0 py-0 text-left text-sm font-semibold"
                      onClick={() => handleSort(column.key)}
                    >
                      {column.label}
                      {renderSortIcon(column.key)}
                    </Button>
                  </TableHead>
                ))}
                <TableHead>Role</TableHead>
                {hasActions && <TableHead>Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {isUsersLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={columnCount}
                    className="h-24 text-center text-sm text-muted-foreground"
                  >
                    Loading users...
                  </TableCell>
                </TableRow>
              ) : users.length > 0 ? (
                users.map((user) => {
                  const isExpanded = expandedUserIds.has(user.id);

                  return (
                    <React.Fragment key={user.id}>
                      <TableRow
                        className={cn(
                          "cursor-pointer",
                          isExpanded && "bg-muted/40"
                        )}
                        onClick={() => toggleExpandedUser(user.id)}
                        onKeyDown={(event) => {
                          if (event.target !== event.currentTarget) {
                            return;
                          }
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            toggleExpandedUser(user.id);
                          }
                        }}
                        tabIndex={0}
                        aria-expanded={isExpanded}
                        aria-controls={`user-org-memberships-${user.id}`}
                      >
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <ChevronDown
                              className={cn(
                                "size-4 text-muted-foreground transition-transform",
                                isExpanded && "rotate-180"
                              )}
                            />
                            <span className="text-sm font-medium text-foreground">
                              {displayUserName(user, "Unknown", {
                                useEmail: false,
                              })}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <span
                              className="text-sm text-muted-foreground"
                              title={user.email ?? undefined}
                            >
                              {displayUserEmail(user.email)}
                            </span>
                            {user.email ? (
                              <CopyButton
                                value={user.email}
                                label="email address"
                              />
                            ) : null}
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-muted-foreground">
                            {format(
                              new Date(user.createdAt),
                              "yyyy-MM-dd HH:mm:ss"
                            )}
                          </span>
                        </TableCell>
                        <TableCell>
                          {renderRole ? (
                            renderRole(user)
                          ) : (
                            <RoleBadge role={user.role} />
                          )}
                        </TableCell>
                        {hasActions && (
                          <TableCell>{renderActions?.(user)}</TableCell>
                        )}
                      </TableRow>
                      {isExpanded && (
                        <TableRow
                          id={`user-org-memberships-${user.id}`}
                          className="bg-muted/20 hover:bg-muted/20"
                        >
                          <TableCell colSpan={columnCount} className="py-3">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="text-xs font-medium uppercase text-muted-foreground">
                                Organisations
                              </span>
                              {user.organisationMemberships.length > 0 ? (
                                user.organisationMemberships.map(
                                  (organisation) => (
                                    <Badge
                                      key={organisation.id}
                                      variant="outline"
                                    >
                                      {organisation.name}
                                    </Badge>
                                  )
                                )
                              ) : (
                                <span className="text-sm text-muted-foreground">
                                  No organisations
                                </span>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columnCount}
                    className="h-24 text-center text-sm text-muted-foreground"
                  >
                    No users match the current search and filters.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      {(page > 0 || hasNextPage) && (
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                href="#"
                onClick={(event) => {
                  event.preventDefault();
                  goToPreviousPage();
                }}
                className={
                  page === 0 ? "pointer-events-none opacity-50" : undefined
                }
                aria-disabled={page === 0}
              />
            </PaginationItem>
            <PaginationItem>
              <span className="px-3 text-sm text-muted-foreground">
                Page {page + 1}
              </span>
            </PaginationItem>
            <PaginationItem>
              <PaginationNext
                href="#"
                onClick={(event) => {
                  event.preventDefault();
                  goToNextPage();
                }}
                className={
                  !hasNextPage ? "pointer-events-none opacity-50" : undefined
                }
                aria-disabled={!hasNextPage}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
    </>
  );
};
